import React, { useState } from 'react';
import styles from './position-status-position-insights-chart.module.scss';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LabelList, Cell } from 'recharts';
import { Popover } from 'antd';
import { useSelector } from 'react-redux';
import { ROLE_NAME } from '../../../../utils/constants';
import { KEY_NAME_POSITION_STATUS, POSITION_INSIGHTS_TAB } from '../../talent-analytics.constant';
import { isEmpty } from 'lodash';

const PositionStatusPositionInsightsChart = ({ data, setStackChartData, handleOnClickReport, type }) => {
  const roleActive = useSelector((state) => state.user.roleActive);
  const [openStackName, setOpenStackName] = useState(null);

  const renderDot = (key) => {
    switch (key) {
      case 'CCP':
        return '#00A19C';
      case 'BCP':
        return '#8B58B1';
      case 'OCP':
        return '#31A5F5';
      case 'OP':
        return '#337CA0';
      default:
        return '';
    }
  };

  const PopOverContent = ({ name, dataKey, value }) => {
    return (
      <div className={styles.popover_content}>
        <div className={styles.title}>{name}</div>
        <div className={styles.row}>
          <span className={styles.dot} style={{ backgroundColor: renderDot(dataKey) }} />
          <span className={styles.label}>{dataKey}:</span>
          <span className={styles.value}>{value}</span>
        </div>
      </div>
    );
  };

  const RoundedBarWithGap = (props) => {
    const { x, y, width, height, fill, payload, dataKey } = props;
    const gap = 4;
    const newHeight = height - gap;

    const value = payload?.[dataKey];
    const nameUnique = `${payload['name']} ${dataKey}`;

    return (
      <foreignObject x={x} y={y + gap} width={width} height={newHeight > 0 ? newHeight : 0}>
        <Popover
          content={<PopOverContent name={payload?.name} dataKey={dataKey} value={value} />}
          placement="top"
          trigger="hover"
          overlayClassName="custom_popover_position_status"
          open={nameUnique === openStackName}
          onOpenChange={(open) => {
            if (open) {
              setOpenStackName(nameUnique);
              setStackChartData((pre) => ({
                ...pre,
                // [POSITION_INSIGHTS_TAB[1]]: {
                [POSITION_INSIGHTS_TAB[0]]: {
                  xAxisName: payload?.name,
                  stackName: dataKey,
                },
              }));
            } else {
              setOpenStackName(null);
            }
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              backgroundColor: fill,
              borderRadius: 4,
              cursor: 'pointer',
            }}
          />
        </Popover>
      </foreignObject>
    );
  };

  const CustomTick = ({ x, y, payload }) => {
    const paddingX = 4;
    const paddingY = 4;
    const fontSize = 12;
    const text = payload.value;

    const textLength = text.length;
    const textWidth = textLength * (fontSize * 0.6);
    const width = textWidth + paddingX * 2;
    const height = fontSize + paddingY * 2;

    return (
      <g transform={`translate(${x - width / 2}, ${y})`}>
        <rect width={width} height={height} fill="#F0F5FA" rx={height / 2} ry={height / 2} />
        <text x={width / 2} y={height / 2 + fontSize / 3} textAnchor="middle" fill="#394655" fontSize={fontSize} fontWeight={400}>
          {text}
        </text>
      </g>
    );
  };

  const filteredDataChart = data?.chart_data?.filter((item) => item.CCP !== 0 || item.BCP !== 0 || item.OCP !== 0 || item.OP !== 0);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.header_wrapper}>
          <div className={styles.header_title}>Positions</div>
          <div className={styles.header_num}>{data?.positions}</div>
        </div>
        <div className={styles.header_wrapper}>
          <div className={styles.header_title}>With Matching Criteria</div>
          <div className={styles.header_num}>{data?.with_matching_criteria}</div>
        </div>
        <div className={styles.header_wrapper}>
          <div className={styles.header_title}>Without Matching Criteria</div>
          <div className={styles.header_num}>{data?.without_matching_criteria}</div>
        </div>
      </div>
      <div className={styles.chart_title}>
        {[KEY_NAME_POSITION_STATUS.POSITION_WITHOUT_SP, KEY_NAME_POSITION_STATUS.POSITION_WITHOUT_SP_NEW_POSITION].includes(type)
          ? 'Position Created Status'
          : 'Position review status'}
      </div>
      <div className={styles.legend_chart_wrapper}>
        {data.chart_data?.[0] &&
          Object.keys(data.chart_data[0])
            .filter((d) => d !== 'name')
            .map((key, i) => (
              <div className={styles.legend_chart} key={i}>
                <div
                  className={styles.legend_square}
                  style={{
                    background: renderDot(key),
                  }}
                ></div>
                <div className={styles.legend_label}>{key}</div>
              </div>
            ))}
      </div>

      <div className={styles.chart_wrapper}>
        <button
          className={styles.btn}
          onClick={handleOnClickReport}
          disabled={![ROLE_NAME.HR_COE, ROLE_NAME.HR_ADMIN].includes(roleActive?.roleName) || data.positions === 0}
        >
          View report
        </button>
        <ResponsiveContainer width="100%" height={600}>
          <BarChart data={filteredDataChart} barSize={50} barCategoryGap={10}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} color="#CECECE" />
            <XAxis dataKey="name" tickLine={false} axisLine={false} fontSize={12} color="#394655" fontWeight={400} tick={<CustomTick />} />
            <YAxis tickLine={false} axisLine={false} domain={[0, 'auto']} fontSize={14} color="#4F5F6E" fontWeight={400} />

            <Bar
              minPointSize={5}
              dataKey="CCP"
              stackId="a"
              fill="#00A19C"
              shape={(props) => <RoundedBarWithGap {...props} />}
              isAnimationActive={false}
            >
              {!isEmpty(filteredDataChart) &&
                filteredDataChart.map((entry, index) => <Cell key={`cell-${index}`} fill={entry?.CCP === 0 ? 'transparent' : '#00A19C'} />)}
              <LabelList dataKey="CCP" position="center" fill="#FFFFFF" fontSize={12} fontWeight={700} />
            </Bar>
            <Bar
              minPointSize={5}
              dataKey="BCP"
              stackId="a"
              fill="#8B58B1"
              shape={(props) => <RoundedBarWithGap {...props} />}
              isAnimationActive={false}
            >
              {!isEmpty(filteredDataChart) &&
                filteredDataChart.map((entry, index) => <Cell key={`cell-${index}`} fill={entry?.BCP === 0 ? 'transparent' : '#8B58B1'} />)}
              <LabelList dataKey="BCP" position="center" fill="#FFFFFF" fontSize={12} fontWeight={700} />
            </Bar>
            <Bar
              minPointSize={5}
              dataKey="OCP"
              stackId="a"
              fill="#31A5F5"
              shape={(props) => <RoundedBarWithGap {...props} />}
              isAnimationActive={false}
            >
              {!isEmpty(filteredDataChart) &&
                filteredDataChart.map((entry, index) => <Cell key={`cell-${index}`} fill={entry?.OCP === 0 ? 'transparent' : '#31A5F5'} />)}
              <LabelList dataKey="OCP" position="center" fill="#FFFFFF" fontSize={12} fontWeight={700} />
            </Bar>
            <Bar
              minPointSize={5}
              dataKey="OP"
              stackId="a"
              fill="#337CA0"
              shape={(props) => <RoundedBarWithGap {...props} />}
              isAnimationActive={false}
            >
              {!isEmpty(filteredDataChart) &&
                filteredDataChart.map((entry, index) => <Cell key={`cell-${index}`} fill={entry?.OP === 0 ? 'transparent' : '#337CA0'} />)}
              <LabelList dataKey="OP" position="center" fill="#FFFFFF" fontSize={12} fontWeight={700} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PositionStatusPositionInsightsChart;
